import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowOrientationSlab,
  DrawControllerWrapper,
  TargetPlane,
  useTapelineSnapTargets,
} from '@scene'
import { isNull } from 'lodash-es'
import { useTheme } from '@mui/material'
import {
  ImmutableVector3,
  TransientDrawState,
  InteractiveLine,
  InteractiveLineRef,
  InteractiveLineHandles,
  sceneConfig,
} from '@modugen/ui-toolkit'
import { useControlStore } from '@editorStores'
import { useClickActionEnabled, useHideElements, useHideFloorplanElements } from '@editorHooks'
import { nthLooped } from '@utils'
import { roofDrawingDecimals } from 'src/components/pages/Editor/config'
import { useModelSnapTargets } from '../../../FloorplanDrawer/hooks'
import { getEdgeExpansionAxis, moveVerticalSlabEdge } from './utils'

interface Props {
  points: ImmutableVector3[]
  updatePoints: (points: ImmutableVector3[]) => void
  element: string
  elementType: 'vertical_slabs' | 'vertical_roof_slabs'
}

const pointsFromEdges = (
  lines: Array<InteractiveLineRef | undefined>,
  fallbackEdges: ReadonlyArray<readonly [ImmutableVector3, ImmutableVector3]>,
) =>
  fallbackEdges.map((edge, index) => {
    const start = lines[index]?.transientLine.current?.[0] ?? edge[0]
    return new ImmutableVector3(start.x, start.y, start.z)
  })

const VerticalSlabEdgeDrawing = ({
  points,
  updatePoints,
  element,
  elementType,
}: Props): ReactElement => {
  const { scenePalette } = useTheme()
  const edgeLineRefs = useRef<Array<InteractiveLineRef>>([])
  const [activeEdgeIndex, setActiveEdgeIndex] = useState<null | number>(null)
  const [previewPoints, setPreviewPoints] = useState<ImmutableVector3[] | null>(null)

  const slabPoints = useMemo(
    () => points.map(point => new ImmutableVector3(point.x, point.y, point.z)),
    [points],
  )

  const edges = useMemo(
    () =>
      slabPoints.map((point, index) => {
        const nextPoint = nthLooped(slabPoints, index + 1)
        return [point, nextPoint] as const
      }),
    [slabPoints],
  )

  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const setElementSelectionDisabled = useControlStore(state => state.setElementSelectionDisabled)

  const tapelineSnapTargets = useTapelineSnapTargets(false)
  const modelSnapTargets = useModelSnapTargets({})
  const allSnapTargets = useMemo(
    () => [...modelSnapTargets, ...tapelineSnapTargets],
    [modelSnapTargets, tapelineSnapTargets],
  )

  useHideElements(element)
  useHideFloorplanElements(element)

  const moveEdge = (state: TransientDrawState) => {
    if (isNull(activeEdgeIndex) || !state.drawPoint) return

    const edge = edgeLineRefs.current[activeEdgeIndex]
    if (!edge?.transientLine.current) return

    const axis = getEdgeExpansionAxis(points, activeEdgeIndex)
    const [nextStart, nextEnd] = moveVerticalSlabEdge(
      slabPoints,
      activeEdgeIndex,
      axis === 'x' ? state.drawPoint.x : state.drawPoint.y,
      roofDrawingDecimals,
    )

    nthLooped(edgeLineRefs.current, activeEdgeIndex - 1).updateHandle(
      InteractiveLineHandles.End,
      nextStart,
    )
    nthLooped(edgeLineRefs.current, activeEdgeIndex + 1).updateHandle(
      InteractiveLineHandles.Start,
      nextEnd,
    )
    edge.updateLine([nextStart, nextEnd])
    setPreviewPoints(pointsFromEdges(edgeLineRefs.current, edges))
  }

  const drawEnd = () => {
    setElementSelectionDisabled(false)
    if (isNull(activeEdgeIndex)) return

    const nextPoints = pointsFromEdges(edgeLineRefs.current, edges)

    updatePoints(nextPoints)
    setPreviewPoints(null)
    setActiveEdgeIndex(null)
  }

  const arrowPoints = previewPoints ?? slabPoints
  const arrowSlab = useMemo(
    () =>
      ({
        element_type: elementType,
        shape: { points: arrowPoints },
      } as ShapeObject),
    [arrowPoints, elementType],
  )
  const arrowColor = scenePalette.elements2d[elementType].outlineColor as string

  const drawControllerEnabled = useClickActionEnabled()

  useEffect(() => {
    return () => setElementSelectionDisabled(false)
  }, [setElementSelectionDisabled])

  if (slabPoints.length !== 4) return <></>

  return (
    <>
      <DrawControllerWrapper
        enabled={!isNull(activeEdgeIndex) && drawControllerEnabled}
        onMouseMove={moveEdge}
        onDrawEnd={drawEnd}
        enableIndicator={false}
        isValidDrawTarget={object => object.name === 'draw-plane'}
        additionalSnapTargets={allSnapTargets}
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
      />

      {edges.map(([start, end], index) => (
        <InteractiveLine
          key={`${element}+${index}-${start.x}-${start.y}-${start.z}-${end.x}-${end.y}-${end.z}-`}
          ref={line => {
            edgeLineRefs.current[index] = line as InteractiveLineRef
          }}
          line={[start, end]}
          color={index === activeEdgeIndex ? 'blue' : '#707070'}
          disableDepthTest
          isSelected={activeEdgeIndex === index}
          onClick={() => {
            setActiveEdgeIndex(index)
            setElementSelectionDisabled(true)
          }}
          showHandles={false}
          nonSelectable={!isNull(activeEdgeIndex)}
          clickDisabled={!isNull(activeEdgeIndex)}
        />
      ))}

      <group position={[0, 0, 0.001]} layers={sceneConfig.R3FNonSelectableObjectLayer}>
        <ArrowOrientationSlab slab={arrowSlab} color={arrowColor} />
      </group>

      <TargetPlane points={slabPoints} />
    </>
  )
}

export default VerticalSlabEdgeDrawing
